import React, { useState } from 'react';
import PropTypes from 'prop-types';
import CartProp from '../../propTypes/CartProp';
import StripePaymentMethodsProp from '../../../payments/propTypes/StripePaymentMethodsProp';
import Request from '../../../../services/request';
import Notifications from '../../../../services/notifications';
import {
  compose,
  colors,
  isHigherAdmin,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STATUS,
} from '../../../../services/utilityFunctions';
import translate from '../../../../commonComponents/translate';
import withCSRF from '../../../../commonComponents/withCSRF';
import withLoggedUser from '../../../../commonComponents/withLoggedUser';
import UserProp from '../../../profile/propTypes/userProp';
import BillingProp from '../../../profile/propTypes/billingProp';
import PaymentMethodTypesSelector from './paymentMethodsTypesSelector/PaymentMethodTypesSelector';
import {
  allowBankTransferPaymentMethod,
  isValidPaymentMethod,
} from '../../../../services/cartCalculations';
import CategoryProp from '../../propTypes/CategoryProp';
import PlanProp from '../../propTypes/PlanProp';

function PaymentForm({
  t,
  _csrf,
  subscriptionPaymentStatus,
  plansAreFree,
  cart,
  categories,
  plans,
  paymentSettings,
  paymentMethodsTypes,
  finishButtonText,
  onFinish,
  showValidationErrors,
  onDiscountAdd,
  isEdit,
  isResellerInstance,
  loggedUser,
  billing,
  updatePaymentMethod,
  handleIsLoading,
  taxRatePercentage,
}) {
  const { packs, products, discounts } = cart;
  const { paymentMethods, paymentMethodId, paymentMethodType } =
    paymentSettings;
  const [coupon, setCoupon] = useState('');
  const [postingForm, setPostingForm] = useState(false);
  const [showError, setShowError] = useState(showValidationErrors);

  const handleCouponChange = evt => {
    setCoupon(evt.currentTarget.value);
  };

  const performRequest = (
    method,
    path,
    params,
    errorKey,
    onSuccess,
    onError,
  ) => {
    const handleError = err => {
      Notifications.showNotificationError(t('error'), err);
      setPostingForm(false);
      if (onError) {
        onError();
      }
    };

    setPostingForm(true);
    const result = new Request(_csrf)[method](path, params);
    try {
      result.done(onSuccess).fail(err => {
        let error = t(errorKey);
        if (err && err.responseJSON) {
          error = err.responseJSON.message || t(err.responseJSON.error);
        }
        handleError(error);
      });
    } catch (err) {
      handleError(err);
    }
  };

  const handleApplyCoupon = () => {
    const onSuccess = result => {
      setCoupon('');
      setPostingForm(false);
      if (!result.data) {
        Notifications.showNotificationError(
          t('error'),
          t('error_applyDiscountCode'),
        );
      } else {
        onDiscountAdd(result.data);
      }
    };

    const onError = () => {
      setCoupon('');
    };

    performRequest(
      'get',
      `/discount/code/${coupon}`,
      undefined,
      'error_applyDiscountCode',
      onSuccess,
      onError,
    );
  };
  const allowBankTransfer = allowBankTransferPaymentMethod(
    cart,
    plans,
    categories,
    taxRatePercentage,
    plansAreFree,
    isHigherAdmin(loggedUser),
  );
  const hasEditPrivilege = !isResellerInstance && isHigherAdmin(loggedUser);
  const noPaymentOnSubscriptions =
    plansAreFree && packs.length === 0 && products.length === 0;
  const requiresPaymentMethod = !noPaymentOnSubscriptions;
  const invalidPaymentMethodsCheck = !paymentMethodType && !paymentMethodId;
  const disableButton = requiresPaymentMethod
    ? invalidPaymentMethodsCheck
    : false;
  const unappliedDiscounts = discounts.filter(d => !d.applied);

  const handleFinishSubmit = () => {
    if (
      !isValidPaymentMethod(
        cart,
        plans,
        categories,
        taxRatePercentage,
        plansAreFree,
        paymentMethodType,
        paymentMethodId,
        isResellerInstance,
        isHigherAdmin(loggedUser),
      )
    ) {
      setShowError(true);
      Notifications.showNotificationError(
        t('error'),
        t('subscriptionCreate_payment_method_required'),
      );
      return;
    }
    setShowError(false);
    onFinish();
  };

  return (
    <>
      <div className="payment-cc-title">
        {t('subscriptionCreate_payment_payment')}
      </div>
      {(subscriptionPaymentStatus === SUBSCRIPTION_STATUS.FAILED ||
        subscriptionPaymentStatus === SUBSCRIPTION_STATUS.CANCELED) && (
        <div className="alert alert-danger" role="alert">
          <h4>{t('subscriptionCreate_payment_unpaid_title')}</h4>
          <p>{t('subscriptionCreate_payment_unpaid_description')}</p>
        </div>
      )}
      {requiresPaymentMethod && showError && (
        <h6
          className="payment-cc-noSelection-title has-danger"
          style={{ color: '#f4516c' }}
        >
          {t('subscriptionCreate_payment_method_required')}
        </h6>
      )}
      {requiresPaymentMethod ? (
        <PaymentMethodTypesSelector
          paymentMethodsTypes={paymentMethodsTypes}
          paymentSettings={paymentSettings}
          updatePaymentMethod={updatePaymentMethod}
          handleIsLoading={handleIsLoading}
          billing={billing}
          allowBankTransfer={allowBankTransfer}
        />
      ) : (
        <h6 className="payment-cc-noSelection-title">
          {t('subscriptionCreate_payment_noCCSelection')}
        </h6>
      )}
      <div className="row">
        <div className="col-12 input-group">
          <input
            disabled={!hasEditPrivilege}
            id="coupon"
            name="coupon"
            value={coupon}
            onChange={handleCouponChange}
            placeholder={t('subscriptionCreate_payment_discountCode')}
            type="text"
            className="form-control"
          />
          <div className="input-group-append">
            <button
              className="btn btn-primary loader-button"
              type="button"
              disabled={!hasEditPrivilege}
              onClick={handleApplyCoupon}
            >
              {t('apply')}
            </button>
          </div>
        </div>
      </div>
      {unappliedDiscounts.length > 0 && (
        <div className="row discount-warning-container">
          <div className="col-12">
            <h4>{t('subscriptionCreate_payment_unusedDiscounts')}</h4>
            <ul>
              {unappliedDiscounts.map(discount => (
                <li key={discount._id} className="discount-warning-item">
                  {`${discount.name}`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {!isEdit && paymentMethods.length === 0 && (
        <div className="row discount-warning-container">
          <div className="col-12">
            <h4 style={{ color: colors.danger }}>
              {t('subscriptionCreate_payment_cardRequired')}
            </h4>
          </div>
        </div>
      )}
      <button
        type="submit"
        onClick={() => handleFinishSubmit()}
        disabled={postingForm || disableButton}
        className={
          'btn m-btn--pill m-btn--air btn-lg m-btn' +
          ' m-btn--custom btn-primary payment-finish-button loader-button'
        }
      >
        {finishButtonText}
      </button>
    </>
  );
}

PaymentForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  loggedUser: PropTypes.shape(UserProp.propType),
  subscriptionPaymentStatus: PropTypes.oneOf(SUBSCRIPTION_STATUSES).isRequired,
  plansAreFree: PropTypes.bool,
  cart: PropTypes.shape(CartProp.propType).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  plans: PropTypes.arrayOf(PropTypes.shape(PlanProp.propType)).isRequired,
  paymentSettings: PropTypes.shape({
    paymentMethods: PropTypes.arrayOf(
      PropTypes.shape(StripePaymentMethodsProp.propType),
    ).isRequired,
    paymentMethodType: PropTypes.string.isRequired,
    paymentMethodId: PropTypes.string.isRequired,
    accountHolderName: PropTypes.string,
  }).isRequired,
  paymentMethodsTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  finishButtonText: PropTypes.string.isRequired,
  onFinish: PropTypes.func.isRequired,
  showValidationErrors: PropTypes.bool,
  onDiscountAdd: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isResellerInstance: PropTypes.bool.isRequired,
  billing: PropTypes.shape(BillingProp.propType),
  updatePaymentMethod: PropTypes.func.isRequired,
  handleIsLoading: PropTypes.func.isRequired,
  taxRatePercentage: PropTypes.number.isRequired,
};

PaymentForm.defaultProps = {
  loggedUser: null,
  plansAreFree: false,
  showValidationErrors: false,
  billing: BillingProp.defaultProp,
};

export default compose(translate, withCSRF, withLoggedUser)(PaymentForm);
